import React, { useMemo, useState } from "react";
import {
  CaretDownOutlined,
  CaretRightOutlined,
  CaretUpOutlined,
} from "@ant-design/icons";
import { typeMap3 } from "@/constants/dataMap";

/** 预览中不展示的字段：内部字段 + 设计/几何类信息（与参数编辑页保持一致） */
const HIDDEN_PARAM_KEYS = new Set<string>([
  "操作单元类型",
  "内部类型",
  "塔盘类型",
  "填料类型",
  "是否使用Murphree效率",
  "工艺名称",
  "设备描述",
  "描述",
  "总高",
  "壳体长度",
  "材质",
  "腐蚀裕量",
  "塔板材质",
  "溢流类型",
  "填料高度",
  "设计塔顶温度",
  "设计塔底温度",
  "设计塔顶压力",
  "设计塔底压力",
  "操作塔顶温度",
  "操作塔底温度",
  "操作塔顶压力",
  "操作塔底压力",
  "设计温度上限",
  "设计温度下限",
  "壁面粗糙度",
]);

/** 元件级基础信息，单独展示，参数区不再重复 */
const BASIC_PARAM_KEYS = new Set<string>(["物性计算方法", "相态", "计算模式"]);

const PHASE_LABELS: Record<string, string> = { L: "L-液相", V: "V-汽相", VL: "VL-汽液两相" };
const MODE_LABELS: Record<string, string> = { TP: "TP-温压闪蒸", VP: "VP-汽化率压力闪蒸", HP: "HP-压焓闪蒸" };

/** 塔板效率表头（与参数编辑页一致） */
const EFF_COLUMNS = [
  { key: "起始塔板", title: "起始塔板", flex: 1 },
  { key: "结束塔板", title: "结束塔板", flex: 1 },
  { key: "效率值", title: "塔板效率", flex: 1 },
  { key: "特殊效率", title: "塔板组分效率", flex: 2 },
];

const isEmptyValue = (value: any) =>
  value === null || value === undefined || value === "";

/** 三列栅格中的一个「标签 + 值」单元 */
const cellStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  flex: "0 0 calc((100% - 40px) / 3)",
  minWidth: 0,
};

const cellLabelStyle: React.CSSProperties = {
  flex: "0 0 auto",
  fontSize: 14,
  color: "#8c8c8c",
  marginRight: 8,
  whiteSpace: "nowrap",
};

const cellValueStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#333",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const cellUnitStyle: React.CSSProperties = {
  fontSize: 13,
  color: "#8c8c8c",
  marginLeft: 4,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: "bold",
  color: "#1b6ae7",
  marginBottom: 8,
};

interface GroupCalParamsPreviewProps {
  jsonData: any;
  selectedElements: string[];
  /** 初始是否收起，默认展开 */
  defaultCollapsed?: boolean;
}

/**
 * 组合计算弹窗的「输入参数预览」
 * 元件页签按类型列出已选元件，展示元件基础信息 + 参数；
 * 流股页签列出与已选元件相关的流股，展示流股参数与组分。
 */
const GroupCalParamsPreview: React.FC<GroupCalParamsPreviewProps> = ({
  jsonData,
  selectedElements,
  defaultCollapsed = false,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [tab, setTab] = useState<"element" | "stream">("element");
  const [curElement, setCurElement] = useState("");
  const [curStream, setCurStream] = useState("");

  //已选中的元件（按画布顺序）
  const elementNodes = useMemo(() => {
    const names = selectedElements || [];
    return (jsonData?.nodeDataArray || []).filter(
      (item: any) =>
        !item.isGroup &&
        item.elementName &&
        item.nodeName?.trim() !== "" &&
        names.includes(item.nodeName),
    );
  }, [jsonData, selectedElements]);

  //元件按类型分组（混合器/分流器/塔...）
  const elementGroups = useMemo(() => {
    const map = new Map<string, any[]>();
    elementNodes.forEach((item: any) => {
      const type = item.type || "未分类";
      if (!map.has(type)) map.set(type, []);
      map.get(type)!.push(item);
    });
    return Array.from(map.entries());
  }, [elementNodes]);

  //左侧元件分类中已收起的类型：初始只展开高亮元件所在的分类
  const [collapsedTypes, setCollapsedTypes] = useState<string[]>(() => {
    const activeType = elementNodes[0]?.type || "未分类";
    return elementGroups
      .map(([type]) => type)
      .filter((type) => type !== activeType);
  });

  const toggleType = (type: string) => {
    setCollapsedTypes((prev) =>
      prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type],
    );
  };

  //与已选元件相关的流股
  const relatedStreams = useMemo(() => {
    const nodeKeys = elementNodes.map((item: any) => item.key);
    return (jsonData?.linkDataArray || []).filter(
      (link: any) =>
        link.linkName &&
        link.linkName.trim() !== "" &&
        (nodeKeys.includes(link.from) || nodeKeys.includes(link.to)),
    );
  }, [jsonData, elementNodes]);

  const activeElement =
    elementNodes.find((item: any) => item.nodeName === curElement) ||
    elementNodes[0];
  const activeStream =
    relatedStreams.find((item: any) => item.linkName === curStream) ||
    relatedStreams[0];

  /** 把一个参数对象拆成「普通参数」与「带子项的表格参数」 */
  const splitParams = (paramValues: any) => {
    const scalars: { key: string; value: any; unit: string }[] = [];
    const childrenParams: { key: string; entry: any }[] = [];

    Object.keys(paramValues || {}).forEach((key) => {
      const entry = paramValues[key] || {};
      if (HIDDEN_PARAM_KEYS.has(key) || BASIC_PARAM_KEYS.has(key)) return;
      if (entry.show === false) return;

      if (entry.p_children) {
        if (entry.p_children.length > 0) childrenParams.push({ key, entry });
        return;
      }
      if (isEmptyValue(entry.value)) return;
      scalars.push({ key, value: entry.value, unit: entry.unit || "" });
    });

    return { scalars, childrenParams };
  };

  /** 渲染三列栅格中的一个单元 */
  const renderCell = (
    label: string,
    value: any,
    unit?: string,
    key?: string,
  ) => (
    <div style={cellStyle} key={key ?? label}>
      <div style={cellLabelStyle}>{label}</div>
      <div style={cellValueStyle}>
        {isEmptyValue(value) ? "—" : String(value)}
        {!isEmptyValue(value) && unit ? (
          <span style={cellUnitStyle}>{unit}</span>
        ) : null}
      </div>
    </div>
  );

  /** 塔板效率表格 */
  const renderEffTable = (children: any[]) => (
    <div style={{ flex: 1, border: "1px solid #e8e8e8", borderRadius: 2 }}>
      <div style={{ display: "flex", backgroundColor: "#f5f7fa" }}>
        {EFF_COLUMNS.map((col) => (
          <div
            key={col.key}
            style={{
              flex: col.flex,
              padding: "6px 8px",
              fontSize: 14,
              fontWeight: "bold",
              color: "#1b6ae7",
              textAlign: "center",
            }}
          >
            {col.title}
          </div>
        ))}
      </div>
      {children.map((child: any, index: number) => (
        <div
          key={index}
          style={{ display: "flex", borderTop: "1px solid #f0f0f0" }}
        >
          {EFF_COLUMNS.map((col) => (
            <div
              key={col.key}
              style={{
                flex: col.flex,
                padding: "6px 8px",
                fontSize: 14,
                color: "#333",
                textAlign: "center",
              }}
            >
              {col.key === "特殊效率"
                ? renderSpecText(child[col.key])
                : isEmptyValue(child[col.key]?.value)
                  ? "—"
                  : String(child[col.key].value)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  /** 特殊效率：无数据时展示「—」，有数据时展示「组分: 效率值」 */
  const renderSpecText = (spec: any) => {
    const pairs = spec?.value;
    if (!Array.isArray(pairs) || pairs.length === 0) return "—";
    return pairs
      .map((pair: any) => {
        const material = spec?.children?.[Number(pair?.[0]) - 1]?.["组分"]?.value;
        return `${material ?? pair?.[0]}: ${pair?.[1]}`;
      })
      .join("；");
  };

  /** 带子项的参数（进料/出料流股位置、输出流量、出入流股关系）：按「流股 + 位置」成对展示 */
  const renderChildrenPair = (children: any[]) => (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        rowGap: 10,
        columnGap: 20,
        flex: 1,
      }}
    >
      {children.map((child: any, index: number) => {
        const keys = Object.keys(child);
        const nameKey = keys[0];
        const valueKey = keys[keys.length - 1];
        return (
          <div style={cellStyle} key={index}>
            <div style={cellLabelStyle}>
              {`${child[nameKey]?.value ?? ""} ${valueKey}`}
            </div>
            <div style={cellValueStyle}>
              {isEmptyValue(child[valueKey]?.value)
                ? "—"
                : String(child[valueKey].value)}
            </div>
          </div>
        );
      })}
    </div>
  );

  /** 元件详情 */
  const renderElementDetail = () => {
    if (!activeElement) {
      return (
        <div style={{ fontSize: 14, color: "#8c8c8c", paddingTop: 20 }}>
          请先选择需要组合计算的单元
        </div>
      );
    }

    const paramValues = activeElement.ParamValues || {};
    const { scalars, childrenParams } = splitParams(paramValues);

    const basics = [
      {
        key: "物性计算方法",
        value: activeElement.physical_method,
        unit: "",
      },
      {
        key: "相态",
        value: PHASE_LABELS[activeElement.phase] ?? activeElement.phase,
        unit: "",
      },
      {
        key: "计算模式",
        value: MODE_LABELS[activeElement.mode] ?? activeElement.mode,
        unit: "",
      },
    ];

    return (
      <div>
        <div style={{ fontSize: 17, fontWeight: "bold", color: "#333" }}>
          {`${activeElement.nodeName}：`}
        </div>
        {activeElement.description ? (
          <div
            style={{
              fontSize: 14,
              color: "#8c8c8c",
              marginTop: 6,
              marginBottom: 16,
              whiteSpace: "pre-wrap",
            }}
          >
            {String(activeElement.description).trim()}
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            rowGap: 12,
            columnGap: 20,
            paddingBottom: 12,
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          {basics.map((item) =>
            renderCell(item.key, item.value, item.unit, item.key),
          )}
          {scalars.map((item) =>
            renderCell(item.key, item.value, item.unit, item.key),
          )}
        </div>

        {childrenParams.map(({ key, entry }) => (
          <div key={key} style={{ marginTop: 16 }}>
            <div style={sectionTitleStyle}>{key}</div>
            {key === "塔板效率"
              ? renderEffTable(entry.p_children)
              : renderChildrenPair(entry.p_children)}
          </div>
        ))}

        {scalars.length === 0 && childrenParams.length === 0 ? (
          <div style={{ fontSize: 14, color: "#8c8c8c", marginTop: 12 }}>
            该单元暂无输入参数
          </div>
        ) : null}
      </div>
    );
  };

  /** 流股详情 */
  const renderStreamDetail = () => {
    if (!activeStream) {
      return (
        <div style={{ fontSize: 14, color: "#8c8c8c", paddingTop: 20 }}>
          暂无与所选单元相关的流股
        </div>
      );
    }

    const paramValues = activeStream.ParamValues || {};
    const scalars: { key: string; value: any; unit: string }[] = [];

    Object.keys(paramValues).forEach((key) => {
      const entry = paramValues[key] || {};
      if (HIDDEN_PARAM_KEYS.has(key)) return;
      if (entry.show === false) return;
      if (entry.p_children) return;
      if (isEmptyValue(entry.value)) return;

      let value = entry.value;
      if (key === "相态") value = PHASE_LABELS[value] ?? value;
      if (key === "计算模式") value = MODE_LABELS[value] ?? value;

      scalars.push({ key, value, unit: entry.unit || "" });
    });

    //组分
    const materials = Object.keys(activeStream.materials || {})
      .map((formula) => ({
        name: activeStream.materials[formula].chineseName || formula,
        value: activeStream.materials[formula].molar_frac,
      }))
      .filter((item) => !isEmptyValue(item.value));

    return (
      <div>
        <div style={{ fontSize: 17, fontWeight: "bold", color: "#333" }}>
          {`${activeStream.linkName}：`}
        </div>
        {activeStream.description ? (
          <div
            style={{
              fontSize: 14,
              color: "#8c8c8c",
              marginTop: 6,
              marginBottom: 16,
              whiteSpace: "pre-wrap",
            }}
          >
            {String(activeStream.description).trim()}
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            rowGap: 12,
            columnGap: 20,
            paddingBottom: 12,
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          {scalars.map((item) => renderCell(item.key, item.value, item.unit, item.key))}
        </div>

        {materials.length > 0 ? (
          <div style={{ marginTop: 16 }}>
            <div style={sectionTitleStyle}>组分</div>
            <div
              style={{ display: "flex", flexWrap: "wrap", rowGap: 12, columnGap: 20 }}
            >
              {materials.map((item) =>
                renderCell(item.name, item.value, "", item.name),
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const tabButtonStyle = (active: boolean): React.CSSProperties => ({
    minWidth: 80,
    height: 30,
    padding: "0 14px",
    fontSize: 14,
    borderRadius: 4,
    cursor: "pointer",
    backgroundColor: active ? "#1b6ae7" : "#fff",
    color: active ? "#fff" : "#5f5f5f",
    border: active ? "1px solid #1b6ae7" : "1px solid #d9d9d9",
  });

  return (
    <div style={{ border: "1px solid #e8e8e8", borderRadius: 4 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 16px",
          backgroundColor: "#fafafa",
          borderBottom: collapsed ? "none" : "1px solid #f0f0f0",
        }}
      >
        <span style={{ fontSize: 15, fontWeight: "bold", color: "#333" }}>
          输入参数预览：
        </span>
        {/* 收起时不展示页签 */}
        {!collapsed ? (
          <>
            <button
              type="button"
              style={tabButtonStyle(tab === "element")}
              onClick={() => setTab("element")}
            >
              单元操作
            </button>
            <button
              type="button"
              style={tabButtonStyle(tab === "stream")}
              onClick={() => setTab("stream")}
            >
              流股
            </button>
          </>
        ) : null}
        {/* 展开/收起按钮 */}
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? "展开" : "收起"}
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 6,
            height: 30,
            padding: "0 14px",
            fontSize: 14,
            borderRadius: 4,
            cursor: "pointer",
            backgroundColor: "#fff",
            color: "#1b6ae7",
            border: "1px solid #1b6ae7",
          }}
        >
          {collapsed ? "展开" : "收起"}
          {collapsed ? (
            <CaretDownOutlined style={{ fontSize: 11 }} />
          ) : (
            <CaretUpOutlined style={{ fontSize: 11 }} />
          )}
        </button>
      </div>

      {!collapsed ? (
        <div style={{ display: "flex", height: 440 }}>
          {/* 左侧：元件/流股列表 */}
          <div
            style={{
              width: 200,
              flex: "0 0 auto",
              overflowY: "auto",
              borderRight: "1px solid #f0f0f0",
              padding: "10px 0",
            }}
          >
            {tab === "element" ? (
              elementGroups.length > 0 ? (
                elementGroups.map(([type, items]) => {
                  const typeCollapsed = collapsedTypes.includes(type);
                  return (
                    <div key={type} style={{ marginBottom: 6 }}>
                      <div
                        onClick={() => toggleType(type)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "4px 12px",
                          fontSize: 14,
                          color: "#333",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        title={typeCollapsed ? "展开" : "收起"}
                      >
                        {typeCollapsed ? (
                          <CaretRightOutlined
                            style={{ fontSize: 11, marginRight: 6 }}
                          />
                        ) : (
                          <CaretDownOutlined
                            style={{ fontSize: 11, marginRight: 6 }}
                          />
                        )}
                        {typeMap3[type] || type}
                      </div>
                      {typeCollapsed
                        ? null
                        : items.map((item: any) => {
                            const active =
                              activeElement?.nodeName === item.nodeName;
                            return (
                              <div
                                key={item.key}
                                onClick={() => setCurElement(item.nodeName)}
                                style={{
                                  padding: "6px 12px 6px 32px",
                                  fontSize: 14,
                                  cursor: "pointer",
                                  color: active ? "#1b6ae7" : "#5f5f5f",
                                  backgroundColor: active
                                    ? "#eaf2ff"
                                    : "transparent",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={item.nodeName}
                              >
                                {item.nodeName}
                              </div>
                            );
                          })}
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: "0 12px", fontSize: 14, color: "#8c8c8c" }}>
                  请先选择单元
                </div>
              )
            ) : relatedStreams.length > 0 ? (
              relatedStreams.map((item: any) => {
                const active = activeStream?.linkName === item.linkName;
                return (
                  <div
                    key={item.key}
                    onClick={() => setCurStream(item.linkName)}
                    style={{
                      padding: "6px 12px",
                      fontSize: 14,
                      cursor: "pointer",
                      color: active ? "#1b6ae7" : "#5f5f5f",
                      backgroundColor: active ? "#eaf2ff" : "transparent",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={item.linkName}
                  >
                    {item.linkName}
                  </div>
                );
              })
            ) : (
              <div style={{ padding: "0 12px", fontSize: 14, color: "#8c8c8c" }}>
                暂无相关流股
              </div>
            )}
          </div>

          {/* 右侧：参数详情 */}
          <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
            {tab === "element" ? renderElementDetail() : renderStreamDetail()}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default GroupCalParamsPreview;
